import React, { FC } from 'react';
import { Tooltip, Asset, HelpIcon } from '@clearcover/atlas';
import { Row, Column, FormInput } from 'Shared/components';
import styles from './styles.module.css';
import { EditCoveragesFormValuesType } from './EditCoveragesForm';
import { getIn, useFormikContext } from 'formik';
import { CoverageFormOptionType } from './types';
import { formatMoney, isNumber } from 'Shared/utils/number';

export const CoverageInput: FC<{
  coverage: CoverageFormOptionType;
  formKey: string;
  type: 'coverage' | 'endorsement';
  disabled?: boolean;
  stackedEndorsements?: Array<CoverageFormOptionType>;
}> = ({ coverage, formKey, type, stackedEndorsements, disabled = false }) => {
  const {
    handleBlur,
    setFieldTouched,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormikContext<EditCoveragesFormValuesType>();

  const hasBeenTouched = getIn(touched, formKey);
  const hasError = getIn(errors, formKey);
  let currentValue = getIn(values, formKey);

  const coverageFormKey = formKey.substring(0, formKey.indexOf('.'));

  const inCoverageOptions = coverage.coverageOptions[currentValue];
  const displayValue =
    inCoverageOptions === undefined && isNumber(currentValue)
      ? `${formatMoney(currentValue, { includeCents: false })} Deductible`
      : currentValue;

  return (
    <Row className={styles.row} key={coverage.identifier}>
      <Column className={styles.coverageTooltip}>
        <span className={styles.coverageHeader}>{coverage.label}</span>
        {coverage.description && (
          <>
            <Tooltip
              tooltip={
                <span className={styles.coverageTooltipText}>
                  {coverage.description}
                </span>
              }
              position="right"
            >
              <Asset
                icon={HelpIcon}
                description={`${coverage.label} information`}
                height={16}
                width={16}
                className={styles.helpIcon}
              />
            </Tooltip>
          </>
        )}
      </Column>
      <Column>
        <FormInput
          variant="selectAutocomplete"
          name={coverage.identifier}
          onChange={(value: string) => {
            setFieldTouched(formKey);
            setFieldValue(formKey, value);
          }}
          onBlur={handleBlur}
          value={
            displayValue ??
            (type === 'endorsement' ? 'waived' : coverage.recommendation)
          }
          errorMessage={hasBeenTouched ? hasError : undefined}
          label="Coverage options"
          data-testid={coverage.identifier}
          options={coverage.coverageOptions}
          disabled={disabled}
          className={styles.coverageInput}
        />
        {stackedEndorsements?.map(endorsement => {
          const endorsementFormKey = `${coverageFormKey}.endorsements.${endorsement.identifier}`;
          const currentValue = getIn(values, endorsementFormKey);
          const hasBeenTouched = getIn(touched, endorsementFormKey);
          const hasError = getIn(errors, endorsementFormKey);

          return (
            <FormInput
              variant="selectAutocomplete"
              name={endorsement.identifier}
              onChange={(value: string) => {
                setFieldTouched(endorsementFormKey);
                setFieldValue(endorsementFormKey, value);
              }}
              value={currentValue}
              errorMessage={hasBeenTouched ? hasError : undefined}
              label=""
              data-testid={endorsement.identifier}
              options={endorsement.coverageOptions}
              disabled={disabled}
              className={styles.coverageInput}
              key={endorsement.identifier}
            />
          );
        })}
      </Column>
    </Row>
  );
};
